import { venuesApi } from './venues.api';
import { itineraryApi } from './itinerary.api';
import { tipsApi } from './tips.api';
import { expensesApi } from './expenses.api';
import type { NextDayUpdate } from './itinerary.api';
import type {
  VenueProposal, VenueDeleteProposal, VenueNotesProposal, ActivityProposal, ActivityMoveProposal,
  DayPlanProposal, ReorderProposal, DeleteProposal, TipsProposal, PackingProposal,
  ExpenseProposal, ExpenseDeleteProposal, BudgetProposal,
} from './ai.api';
import type { AiDataArea, AiEntity, AiOperation } from './ai.api';
import type { Venue, Activity, Tip, ChecklistSection, Expense, Trip, VenueCategory, ActivityType, ExpenseCategory, ContentSource, BudgetLimit, BudgetLimitInput } from '../types';
import { tripMemberIds } from '../utils/tripMembers';

// Persisting a proposal (whatever its origin — a review card the user clicked
// Accept on, or an auto-accepted trip-onboarding batch) is the same API-call
// glue either way. Shared here so AiChatPanel and the onboarding flow don't
// duplicate the field mapping.

export type AiPersistedAction =
  | { type: 'venue_created';          data: Venue }
  | { type: 'venues_deleted';         venueIds: string[] }
  | { type: 'venue_updated';          data: Venue }
  | { type: 'activity_created';       data: Activity; dayId: string; nextDayUpdate: NextDayUpdate | null }
  | { type: 'activity_moved';         data: Activity; dayId: string; fromActivityId: string }
  | { type: 'activity_deleted';       dayId: string; activityId: string }
  | { type: 'day_plan_applied';       dayId: string; activities: Activity[]; nextDayUpdate: NextDayUpdate | null }
  | { type: 'activities_reordered';   dayId: string; activityIds: string[]; nextDayUpdate: NextDayUpdate | null }
  | { type: 'tips_created';           data: Tip[] }
  | { type: 'packing_sections_created'; data: ChecklistSection[] }
  | { type: 'expense_created';        data: Expense }
  | { type: 'expenses_deleted';       expenseIds: string[] }
  | { type: 'budget_set';             data: BudgetLimit[] }
  | { type: 'activity_updated';       data: Activity; dayId: string }
  // Saved where the owning tab refetches on mount; nothing to patch locally.
  | { type: 'packing_items_updated' }
  | { type: 'note_created' }
  | { type: 'booking_created' }
  | { type: 'meals_updated' }
  // ADR-0028: applied by the server; the owning tab re-reads its data.
  | { type: 'entity_changed'; entity: AiEntity; area: AiDataArea }
  | { type: 'operation_applied'; op: AiOperation; area: AiDataArea };

// `source` marks provenance on persist: 'AI' for accepted chat/auto-suggest
// proposals (the default), 'IMPORT' when replaying a plan extracted from the
// user's uploaded document (the fixed base the AI must not later reorganize).
// Shared venue-create glue: forwards the coords/placeId the AI enrichment
// already resolved so the server doesn't re-fetch them from Google on create.
// Accepts any proposal carrying the persistable venue fields (a VenueProposal
// review card, or a NewVenueProposal scheduled inline in the itinerary).
type VenueCreateFields = {
  name: string; category: string;
  zone?: string | null; address?: string | null; mapsUrl?: string | null;
  placeId?: string | null; latitude?: number | null; longitude?: number | null; notes?: string | null;
  bookingUrl?: string | null;
};
async function createVenueFromProposal(tripId: string, data: VenueCreateFields, source: ContentSource): Promise<Venue> {
  return venuesApi.create(tripId, {
    name:      data.name,
    category:  data.category as VenueCategory,
    zone:      data.zone ?? null,
    address:   data.address ?? null,
    mapsUrl:   data.mapsUrl ?? null,
    placeId:   data.placeId ?? null,
    latitude:  data.latitude ?? null,
    longitude: data.longitude ?? null,
    notes:     data.notes ?? null,
    bookingUrl: data.bookingUrl ?? null,
    source,
  });
}

export async function persistVenueProposal(tripId: string, data: VenueProposal, source: ContentSource = 'AI'): Promise<AiPersistedAction> {
  const venue = await createVenueFromProposal(tripId, data, source);
  return { type: 'venue_created', data: venue };
}

/**
 * Only `notes` travels in the PATCH. The proposal knows nothing about the rest
 * of the venue, and sending fields the AI never looked at is how an edit to one
 * thing quietly reverts another.
 */
export async function persistVenueNotesProposal(tripId: string, data: VenueNotesProposal): Promise<AiPersistedAction> {
  const venue = await venuesApi.update(tripId, data.venueId, { notes: data.after || null });
  return { type: 'venue_updated', data: venue };
}

// Bulk by nature ("empty the list" is one card for 30+ places), so one failing
// delete must not discard the ones that worked: the card is consumed and the tab
// drops exactly the venues that are really gone. Only a total failure throws, so
// the panel can put the card back and say the save failed.
export async function persistVenueDeleteProposal(tripId: string, data: VenueDeleteProposal): Promise<AiPersistedAction> {
  const venueIds: string[] = [];
  for (const venue of data.venues) {
    try {
      await venuesApi.delete(tripId, venue.id);
      venueIds.push(venue.id);
    } catch (e) {
      console.error('[ai] venue delete failed', venue.id, e);
    }
  }
  if (venueIds.length === 0) throw new Error('VENUE_DELETE_FAILED');
  return { type: 'venues_deleted', venueIds };
}

// Returns an array: when the activity schedules a NEW place (`newVenue`), it's
// first created in the Places list and the activity is linked to it — so the
// caller gets both a venue_created (refreshes Places) and activity_created
// (refreshes the itinerary) from a single accepted proposal.
export async function persistActivityProposal(tripId: string, data: ActivityProposal, source: ContentSource = 'AI'): Promise<AiPersistedAction[]> {
  const results: AiPersistedAction[] = [];
  let venueId = data.venueId ?? null;
  if (data.newVenue) {
    const venue = await createVenueFromProposal(tripId, data.newVenue, source);
    venueId = venue.id;
    results.push({ type: 'venue_created', data: venue });
  }
  const { activity, nextDayUpdate, dayActivities } = await itineraryApi.createActivity(tripId, data.dayId, {
    title:        data.title,
    type:         data.type as ActivityType,
    startTime:    data.startTime ?? null,
    durationMins: data.durationMins ?? null,
    venueName:    data.venueName ?? null,
    venueId,
    zone:         data.zone ?? null,
    address:      data.address ?? data.newVenue?.address ?? null,
    notes:        data.notes ?? null,
    bookingUrl:   data.bookingUrl ?? null,
    latitude:     data.latitude ?? data.newVenue?.latitude ?? null,
    longitude:    data.longitude ?? data.newVenue?.longitude ?? null,
    placeId:      data.placeId ?? data.newVenue?.placeId ?? null,
    source,
  });
  results.push({ type: 'activity_created', data: activity, dayId: data.dayId, nextDayUpdate });

  // The model can say where the stop goes ("between these two"), and the server
  // always appends — so a proposal that carries a position needs the same second
  // pass a cross-day drag does (useCrossDayMove: "the server appends and then
  // re-sorts the timed stops, so a drop into the middle needs a second pass to
  // hold its slot"). One insertion, with every existing stop keeping its relative
  // order: this is not the reorganisation the IMPORT lock forbids.
  const reorder = await reorderAfterAnchor(tripId, data, activity, dayActivities);
  if (reorder) results.push(reorder);
  return results;
}

/**
 * Puts the new stop right after the one the proposal named. Silently does
 * nothing when the anchor is gone — the stop stays where the server put it,
 * which is where it would have landed anyway.
 */
async function reorderAfterAnchor(
  tripId: string, data: ActivityProposal, created: Activity, dayActivities: Activity[] | null | undefined,
): Promise<AiPersistedAction | null> {
  if (!data.afterActivityId || !dayActivities) return null;

  const stops = dayActivities.filter(a => !a.isOrigin);
  const rest  = stops.filter(a => a.id !== created.id);
  const at    = rest.findIndex(a => a.id === data.afterActivityId);
  if (at < 0) return null;

  const desired = [...rest.slice(0, at + 1), created, ...rest.slice(at + 1)].map(a => a.id);
  if (desired.join() === stops.map(a => a.id).join()) return null;

  const { nextDayUpdate } = await itineraryApi.reorderActivities(tripId, data.dayId, desired);
  return { type: 'activities_reordered', dayId: data.dayId, activityIds: desired, nextDayUpdate };
}

export async function persistActivityMoveProposal(tripId: string, data: ActivityMoveProposal): Promise<AiPersistedAction> {
  const activity = await itineraryApi.updateActivity(tripId, data.fromDayId, data.activityId, { dayId: data.toDayId });
  // A card generated by a booking is deleted and rebuilt on the target day, so
  // what comes back is a different row — the caller needs the id that left.
  return { type: 'activity_moved', data: activity, dayId: activity.dayId, fromActivityId: data.activityId };
}

// Returns an array: any activity scheduling a NEW place (`newVenue`) also yields
// a venue_created (created in Places first, then linked), prepended before the
// day_plan_applied so both the Places tab and the itinerary refresh from one card.
export async function persistDayPlanProposal(tripId: string, data: DayPlanProposal, source: ContentSource = 'AI'): Promise<AiPersistedAction[]> {
  const venueActions: AiPersistedAction[] = [];
  const created: Activity[] = [];
  let lastNextDayUpdate: NextDayUpdate | null = null;
  for (const act of data.activities) {
    let venueId = act.venueId ?? null;
    if (act.newVenue) {
      const venue = await createVenueFromProposal(tripId, act.newVenue, source);
      venueId = venue.id;
      venueActions.push({ type: 'venue_created', data: venue });
    }
    const { activity, nextDayUpdate } = await itineraryApi.createActivity(tripId, data.dayId, {
      title:        act.title,
      type:         act.type as ActivityType,
      startTime:    act.startTime ?? null,
      durationMins: act.durationMins ?? null,
      venueName:    act.venueName ?? null,
      venueId,
      zone:         act.zone ?? null,
      address:      act.address ?? act.newVenue?.address ?? null,
      notes:        act.notes ?? null,
      bookingUrl:   act.bookingUrl ?? null,
      latitude:     act.latitude ?? act.newVenue?.latitude ?? null,
      longitude:    act.longitude ?? act.newVenue?.longitude ?? null,
      placeId:      act.placeId ?? act.newVenue?.placeId ?? null,
      source,
    });
    created.push(activity);
    if (nextDayUpdate) lastNextDayUpdate = nextDayUpdate;
  }
  return [...venueActions, { type: 'day_plan_applied', dayId: data.dayId, activities: created, nextDayUpdate: lastNextDayUpdate }];
}

export async function persistReorderProposal(tripId: string, data: ReorderProposal): Promise<AiPersistedAction> {
  const { nextDayUpdate } = await itineraryApi.reorderActivities(tripId, data.dayId, data.activityIds);
  return { type: 'activities_reordered', dayId: data.dayId, activityIds: data.activityIds, nextDayUpdate };
}

export async function persistDeleteProposal(tripId: string, data: DeleteProposal): Promise<AiPersistedAction> {
  await itineraryApi.deleteActivity(tripId, data.dayId, data.activityId);
  return { type: 'activity_deleted', dayId: data.dayId, activityId: data.activityId };
}

export async function persistTipsProposal(tripId: string, data: TipsProposal, source: ContentSource = 'AI'): Promise<AiPersistedAction> {
  const created: Tip[] = [];
  for (const t of data.tips) {
    const tip = await tipsApi.create(tripId, { category: t.category, content: t.content, source });
    created.push(tip);
  }
  return { type: 'tips_created', data: created };
}

export async function persistExpenseProposal(tripId: string, trip: Trip, data: ExpenseProposal, source: ContentSource = 'AI'): Promise<AiPersistedAction> {
  // Mirror the manual expense form's defaults: payer is the current user (set
  // server-side), and the amount is split equally among all trip members with
  // any rounding remainder given to the first. The AI never decides these.
  const memberIds = tripMemberIds(trip);
  const total = data.amount;
  const each = parseFloat((total / memberIds.length).toFixed(2));
  const remainder = parseFloat((total - each * memberIds.length).toFixed(2));
  const splits = memberIds.map((userId, i) => ({ userId, amount: i === 0 ? each + remainder : each }));

  const payload = {
    description:   data.description,
    category:      data.category as ExpenseCategory,
    amount:        total,
    currency:      trip.budgetCurrency || 'EUR',
    paymentMethod: 'CARD',
    date:          new Date().toISOString().split('T')[0],
    notes:         data.notes ?? null,
    source,
    splits,
  } as Partial<Expense> & { splits: { userId: string; amount: number }[] };
  const expense = await expensesApi.create(tripId, payload);
  return { type: 'expense_created', data: expense };
}

// Bulk by nature ("borra todos los gastos" is one card for the whole ledger), so
// one failing delete must not discard the ones that worked — same contract as
// persistVenueDeleteProposal. Only a total failure throws, so the panel can put
// the card back and say the save failed.
export async function persistExpenseDeleteProposal(tripId: string, data: ExpenseDeleteProposal): Promise<AiPersistedAction> {
  const expenseIds: string[] = [];
  for (const expense of data.expenses) {
    try {
      await expensesApi.delete(tripId, expense.id);
      expenseIds.push(expense.id);
    } catch (e) {
      console.error('[ai] expense delete failed', expense.id, e);
    }
  }
  if (expenseIds.length === 0) throw new Error('EXPENSE_DELETE_FAILED');
  // Debts are derived from the expenses, so removing one leaves them claiming
  // money nobody owes any more. Recomputed here rather than in the Expenses tab
  // because the proposal can be accepted from any tab (the panel is global) and
  // the tab's own reload never recomputes when the ledger ends up empty.
  // Best-effort: the deletes already went through, so a failed recompute must
  // not read as "the card failed to save".
  try {
    await expensesApi.recalculateDebts(tripId);
  } catch (e) {
    console.error('[ai] debt recalculation after expense delete failed', e);
  }
  return { type: 'expenses_deleted', expenseIds };
}

// Stable identity of a limit across its dimensions — mirrors the server's
// identityKey so an AI-proposed cap replaces the matching existing one instead
// of duplicating it.
function budgetIdentity(l: { scope: string; category?: string | null; userId?: string | null }): string {
  return `${l.scope}|${l.category ?? ''}|${l.userId ?? ''}`;
}

// setBudgets REPLACES the whole limit set, so we first read the current limits
// and merge the AI's proposals on top by identity — never wiping caps (e.g.
// per-person) the user set by hand that the AI can't propose.
export async function persistBudgetProposal(tripId: string, data: BudgetProposal): Promise<AiPersistedAction> {
  const current = await expensesApi.budgets(tripId);
  const merged = new Map<string, BudgetLimitInput>();
  for (const l of current.limits) {
    merged.set(budgetIdentity(l), { scope: l.scope, category: l.category, userId: l.userId, amount: l.amount });
  }
  for (const l of data.limits) {
    const category = l.scope === 'CATEGORY' ? (l.category as ExpenseCategory | null) ?? null : null;
    merged.set(budgetIdentity({ scope: l.scope, category }), { scope: l.scope, category, amount: l.amount });
  }
  const res = await expensesApi.setBudgets(tripId, [...merged.values()]);
  return { type: 'budget_set', data: res.limits };
}

export async function persistPackingProposal(tripId: string, data: PackingProposal, source: ContentSource = 'AI'): Promise<AiPersistedAction> {
  const created: ChecklistSection[] = [];
  for (const sec of data.sections) {
    // The assistant packs for whoever is talking to it, so its sections land in
    // that person's suitcase — not in the trip's shared list.
    const section = await tipsApi.createSection(tripId, { title: sec.title, source, scope: 'personal' });
    const items = [];
    for (const text of sec.items) {
      const item = await tipsApi.createItem(tripId, section.id, { text });
      items.push(item);
    }
    created.push({ ...section, items });
  }
  return { type: 'packing_sections_created', data: created };
}
